import { Directive, EventEmitter, Input, OnDestroy, Optional, Output, Self } from '@angular/core';
import { NgModel } from '@angular/forms';
import { ValidationType } from 'enums/validationType';
import { FormValidationService } from 'services/form-validation.service';
import { MatRadioGroup } from '@angular/material/radio';
import 'extensions/validation.ts';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';

@Directive({ selector: 'input[ngModel], textarea[ngModel], select[ngModel], mat-slide-toggle[ngModel], mat-radio-group' })
export class ValidationDirective implements OnDestroy {

	@Input('dirty')
	set dirty(value: boolean) {
		if (value) {
			this.ngModel?.control.markAllAsDirty();
			this.ngModel?.control.markAllAsTouched();
		}
		this.validate();
	}
	@Output('dirtyChange')
	dirtyState = new EventEmitter<boolean>();

	@Input('validation')
	set validationType(value: ValidationType) {
		this._validationType = value;
		this.validate();
	}
	private _validationType?: ValidationType;

	@Input('required')
	set required(value: boolean) {
		this._required = value;
		this.validate();
	}
	private _required = false;

	@Input('customValidState')
	set customValidState(value: boolean | undefined) {
		this._customValidState = value;
		this.validate();
	}
	private _customValidState? : boolean;

	@Input() ignoreFormValidation = false;

	@Output('validationStateChange')
	validationState = new EventEmitter<boolean>();

	constructor(
		@Optional() private formValidation?: FormValidationService,
		@Optional() @Self() private ngModel?: NgModel,
		@Optional() @Self() matRadioGroup?: MatRadioGroup,
		@Optional() private customInput?: CustomInputComponent) {

		this.validationState.subscribe((state: boolean) => {
			if (this.ignoreFormValidation) return;
			if (formValidation) formValidation.fieldStateChanged(this, state, (ngModel && ngModel.pristine !== null) ? ngModel.pristine : false);
		});

		const validateOnChange = () => {
			if (this.ignoreFormValidation) return;
			this.validate();
		};

		if (ngModel?.update) ngModel.update.subscribe(validateOnChange);
		if (ngModel?.valueChanges) ngModel.valueChanges.subscribe(validateOnChange);
		if (matRadioGroup?.change) matRadioGroup.change.subscribe(validateOnChange);
	}
	ngOnDestroy(): void {
		if (this.formValidation) this.formValidation.removeField(this);
	}
	
	markAsPristine() {
		this.ngModel?.control.markAsPristine();
		this.dirtyState.emit(false);
		this.validate();
	}
	markAsTouched() { this.dirty = true; this.dirtyState.emit(true); }
	
	validate(): void {
		const isValid = this.getValidationResult() && (this._customValidState || this._customValidState === undefined);
		this.validationState.emit(isValid);
		this.ngModel?.control.setErrors(!isValid ? { valid: false, custom: true } : null);
		this.customInput?.validateField();
	}

	private getValidationResult(): boolean {

		if (this.ngModel?.errors && !this.ngModel.errors.custom) return false;

		const value = this.ngModel?.viewModel;
		if (!value) return !this._required;

		switch (this._validationType) {
			case ValidationType.Email:
				return value.isValidEmailAddress();
			case ValidationType.Phone:
				return value.isValidPhoneNumber();
			case ValidationType.Zip:
				return value.isValidZipcode();
			case ValidationType.VatNumber:
				return value.isValidVatNumber();
			case ValidationType.DomainNonDk:
				return value.isValidDomainNonDk();
			case ValidationType.DomainDk:
				return value.isValidDomainDk();
			case ValidationType.Domain:
				return value.isValidDomain();
			case ValidationType.Percent:
				return value.isValidPercent();
			case ValidationType.EAN13:
				return value.isValidEan13();
			case ValidationType.Folder:
				return value.isValidFolder();
			default:
				return true;
		}
	}
}
